import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import { api } from '@/lib/api';
import { usePermission } from '@/Hooks/usePermission';
import { useBranches } from '@/Pages/Operations/shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select, Textarea } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import {
  apiError,
  AvailabilityKind,
  weekdayKeyForIndex,
  type AvailabilityWindow,
  type CreateAvailabilityInput,
  type EmployeeRef,
} from './schedulingLib';

type Form = 'weekly' | 'dated';

/**
 * What a member of staff can and cannot work — the input the conflict
 * detector reads to raise `EMPLOYEE_UNAVAILABLE` while a manager is building
 * the roster.
 *
 * Add-only, by contract, not by choice: `POST
 * /scheduling/employees/:employeeId/availability` lets a manager record a
 * window somebody told them in person, but the controller has no matching
 * DELETE for anyone but the employee themselves
 * (`DELETE /scheduling/me/availability/:id`, gated on `scheduling.view_own`).
 * A withdrawn window has to come from the person it belongs to, through
 * their own screen — there is nothing here to wire a delete button to.
 */
export default function AvailabilityTab() {
  const { t } = useTranslation();
  const { can } = usePermission();
  const canManage = can('scheduling.manage');
  const branches = useBranches();

  const [branchId, setBranchId] = useState('');
  const [employees, setEmployees] = useState<EmployeeRef[]>([]);
  const [employeeId, setEmployeeId] = useState('');
  const [rows, setRows] = useState<AvailabilityWindow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [kind, setKind] = useState<AvailabilityKind>(AvailabilityKind.UNAVAILABLE);
  const [form, setForm] = useState<Form>('weekly');
  const [dayOfWeek, setDayOfWeek] = useState('1');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get('/employees', { params: { branchId: branchId || undefined, status: 'ACTIVE', limit: 200 } })
      .then(({ data }) => setEmployees((data?.data ?? []) as EmployeeRef[]))
      .catch(() => setEmployees([]));
  }, [branchId]);

  const load = useCallback(() => {
    if (!employeeId) {
      setRows([]);
      return;
    }
    setLoading(true);
    setError(null);
    api
      .get<AvailabilityWindow[]>(`/scheduling/employees/${employeeId}/availability`)
      .then(({ data }) => setRows(data ?? []))
      .catch((e) => setError(apiError(e, t('scheduling.availability.loadFailed'))))
      .finally(() => setLoading(false));
  }, [employeeId, t]);

  useEffect(load, [load]);

  const openAdd = () => {
    setKind(AvailabilityKind.UNAVAILABLE);
    setForm('weekly');
    setDayOfWeek('1');
    setStartTime('');
    setEndTime('');
    setStartsAt('');
    setEndsAt('');
    setReason('');
    setFormOpen(true);
  };

  const submit = async () => {
    setSaving(true);
    const payload: CreateAvailabilityInput =
      form === 'weekly'
        ? {
            kind,
            dayOfWeek: Number(dayOfWeek),
            startTime: startTime || undefined,
            endTime: endTime || undefined,
            reason: reason.trim() || undefined,
          }
        : {
            kind,
            startsAt: startsAt ? new Date(startsAt).toISOString() : undefined,
            endsAt: endsAt ? new Date(endsAt).toISOString() : undefined,
            reason: reason.trim() || undefined,
          };
    try {
      await api.post(`/scheduling/employees/${employeeId}/availability`, payload);
      toast.success(t('scheduling.availability.added'));
      setFormOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e, t('scheduling.availability.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <Card>
        <CardContent className="flex flex-wrap items-end justify-between gap-3 p-4">
          <div className="flex flex-wrap items-end gap-3">
            {branches.length > 0 && (
              <Field label={t('scheduling.roster.branch')}>
                <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                  <option value="">{t('workforce.timesheets.anyBranch')}</option>
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
            <Field label={t('scheduling.availability.employee')}>
              <Select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
                <option value="">{t('scheduling.availability.pickEmployee')}</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.firstName} {emp.lastName ?? ''} ({emp.employeeCode})
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          {canManage && employeeId && (
            <Button size="sm" onClick={openAdd}>
              <Plus className="h-4 w-4" />
              {t('scheduling.availability.addWindow')}
            </Button>
          )}
        </CardContent>
      </Card>

      {!employeeId ? (
        <EmptyState
          title={t('scheduling.availability.pickEmployee')}
          hint={t('scheduling.availability.pickEmployeeHint')}
        />
      ) : loading ? (
        <LoadingState label={t('common.loading')} />
      ) : error ? (
        <ErrorState message={error} onRetry={load} />
      ) : rows.length === 0 ? (
        <EmptyState
          title={t('scheduling.availability.empty')}
          hint={t('scheduling.availability.emptyHint')}
          action={
            canManage ? (
              <Button size="sm" onClick={openAdd}>
                {t('scheduling.availability.addWindow')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="grid gap-2 sm:grid-cols-2">
          {rows.map((w) => (
            <Card key={w.id}>
              <CardContent className="space-y-1 p-3">
                <div className="flex items-center justify-between gap-2">
                  <Badge tone={w.kind === AvailabilityKind.AVAILABLE ? 'success' : 'warning'}>
                    {t(`scheduling.availabilityKind.${w.kind}`)}
                  </Badge>
                  <span className="text-xs text-ink-muted">
                    {t(w.startsAt ? 'scheduling.availability.datedForm' : 'scheduling.availability.weeklyForm')}
                  </span>
                </div>
                <p className="text-sm font-medium">
                  {w.startsAt && w.endsAt
                    ? `${new Date(w.startsAt).toLocaleString()} – ${new Date(w.endsAt).toLocaleString()}`
                    : w.dayOfWeek !== null
                      ? `${t(weekdayKeyForIndex(w.dayOfWeek))} ${w.startTime && w.endTime ? `${w.startTime}–${w.endTime}` : t('scheduling.availability.allDay')}`
                      : '—'}
                </p>
                {w.reason && <p className="text-sm text-ink-muted">{w.reason}</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={t('scheduling.availability.addWindow')}
        className="max-w-md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setFormOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button disabled={saving} onClick={() => void submit()}>
              {t('common.save')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Field label={t('scheduling.availability.kind')}>
            <Select value={kind} onChange={(e) => setKind(e.target.value as AvailabilityKind)}>
              <option value={AvailabilityKind.UNAVAILABLE}>{t('scheduling.availabilityKind.UNAVAILABLE')}</option>
              <option value={AvailabilityKind.AVAILABLE}>{t('scheduling.availabilityKind.AVAILABLE')}</option>
            </Select>
          </Field>
          <Field label={t('scheduling.availability.form')}>
            <Select value={form} onChange={(e) => setForm(e.target.value as Form)}>
              <option value="weekly">{t('scheduling.availability.weeklyForm')}</option>
              <option value="dated">{t('scheduling.availability.datedForm')}</option>
            </Select>
          </Field>

          {form === 'weekly' ? (
            <>
              <Field label={t('scheduling.availability.dayOfWeek')}>
                <Select value={dayOfWeek} onChange={(e) => setDayOfWeek(e.target.value)}>
                  {[0, 1, 2, 3, 4, 5, 6].map((d) => (
                    <option key={d} value={d}>
                      {t(weekdayKeyForIndex(d))}
                    </option>
                  ))}
                </Select>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t('scheduling.shift.startTime')} hint={t('scheduling.availability.allDayHint')}>
                  <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
                </Field>
                <Field label={t('scheduling.shift.endTime')}>
                  <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
                </Field>
              </div>
            </>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <Field label={t('scheduling.availability.from')}>
                <Input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
              </Field>
              <Field label={t('scheduling.availability.to')}>
                <Input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
              </Field>
            </div>
          )}

          <Field label={t('scheduling.availability.reason')}>
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
        </div>
      </Dialog>
    </div>
  );
}
